import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from '../utils/axiosConfig';  // 使用配置好的 axios 實例
import API_URL from '../config';  // 導入 API_URL

// 創建一個全局的快取物件
const seoDataCache = {};

/**
 * 生成唯一的 meta id
 * @param {string} type - meta 標籤類型 (og, twitter, article 等)
 * @param {string} name - meta 標籤名稱
 * @param {string} [suffix] - 額外的後綴（用於陣列或特殊情況）
 * @returns {string} 格式化的 meta id
 */
const generateMetaId = (type, name, suffix = '') => {
  const base = `meta-${type}-${name}`.toLowerCase();
  return suffix ? `${base}-${suffix}` : base;
};

/**
 * 處理自定義 meta 標籤的 id
 * @param {Object} meta - meta 標籤物件
 * @param {number} index - 陣列索引（用於生成唯一 id）
 * @returns {Object} 包含 id 的 meta 標籤物件
 */
const processCustomMeta = (meta, index) => {
  if (meta.id) return meta;

  // 決定 meta 類型
  const type = meta.property ? meta.property.split(':')[0] : 'custom';
  
  // 決定 meta 名稱
  const name = meta.property ? 
    meta.property.split(':').slice(1).join('-') : 
    meta.name;

  // 生成唯一 id
  const id = generateMetaId(type, name, index.toString());

  return { ...meta, id };
};

/**
 * SEO Component for managing all meta tags
 * @param {Object} props - Component props
 */
const SEO = ({ 
  // === Basic SEO Props ===
  title = null,
  description = null,
  keywords = null,
  author = '',
  locale = 'zh-TW',
  noIndex = false,
  
  // === Open Graph Props ===
  type = 'website',
  siteName = 'Damen KYT',
  image,
  url = null,
  ogTitle = null,
  ogDescription = null,
  ogImage = null,
  ogType = null,
  ogUrl = null,
  
  // === Article Specific Props ===
  publishedTime = null,
  modifiedTime = null,
  section = null,
  tags = [],
  
  // === Image Props ===
  imageWidth = null,
  imageHeight = null,
  imageAlt = null,
  imageSecureUrl = null,
  
  // === Twitter Card Props ===
  twitterCard = 'summary_large_image',
  twitterSite = null,
  twitterCreator = null,
  twitterTitle = null,
  twitterDescription = null,
  twitterImage = null,
  twitterImageAlt = null,
  
  // === Verification Props ===
  googleSiteVerification = null,
  msvalidate = null,
  domainVerify = null,
  
  // === URL Props ===
  canonicalUrl = null,
  alternateUrls = null,
  
  // === Additional Props ===
  dnsPrefetch = [],
  rssFeeds = [],
  customMeta = [],

  // === Page Path Props ===
  path = null,

  // === New Props ===
  robots = null,
  enableDynamicFill = false
}) => {
  const [seoData, setSeoData] = useState({
    title: '',
    description: '',
    keywords: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    canonicalUrl: '',
    robots: 'index, follow'
  });

  useEffect(() => {
    const fetchSEOData = async () => {
      try {
        // 如果提供了 path，則從 API 獲取 SEO 數據
        if (path) {
          // console.log('[SEO] 開始獲取 SEO 數據:', {
          //   path,
          //   enableDynamicFill,
          //   currentLocation: typeof window !== 'undefined' ? window.location.pathname : null
          // });
          
          // 簡化路徑處理邏輯
          let apiPath = path;
          
          // 標準化路徑格式
          apiPath = apiPath.replace(/\/+/g, '/');
          apiPath = apiPath.startsWith('/') ? apiPath : `/${apiPath}`;
          apiPath = apiPath.endsWith('/') && apiPath !== '/' ? apiPath.slice(0, -1) : apiPath;
          
          // console.log('[SEO] 路徑處理:', {
          //   originalPath: path,
          //   processedPath: apiPath,
          //   enableDynamicFill
          // });
          
          // 構建 API URL
          const apiUrl = `${API_URL}/api/seo-settings${apiPath}`;
          // console.log('[SEO] 發送請求到:', apiUrl);
          
          try {
            const response = await axios.get(apiUrl);
            // console.log('[SEO] API 響應:', {
            //   success: response.data.success,
            //   status: response.status,
            //   data: response.data.data,
            //   enableDynamicFill: response.data.data?.enableDynamicFill
            // });
            
            if (!response.data.success) {
              throw new Error(response.data.error?.message || '獲取 SEO 設定失敗');
            }
            
            const data = response.data.data;
            
            // 合併數據：靜態優先、動態補足、最後預設
            const mergedData = {
              pagePath: data.pagePath || apiPath,
              title: data.title || title || siteName,
              description: data.description || description || '',
              keywords: data.keywords || keywords || '',
              ogTitle: data.ogTitle || ogTitle || data.title || title || siteName,
              ogDescription: data.ogDescription || ogDescription || data.description || description || '',
              ogImage: data.ogImage || ogImage || '',
              twitterTitle: data.twitterTitle || twitterTitle || data.ogTitle || ogTitle || data.title || title || siteName,
              twitterDescription: data.twitterDescription || twitterDescription || data.ogDescription || ogDescription || data.description || description || '',
              twitterImage: data.twitterImage || twitterImage || data.ogImage || ogImage || '',
              twitterCard: data.twitterCard || twitterCard || 'summary_large_image',
              canonicalUrl: data.canonicalUrl || canonicalUrl || `${window.location.origin}${apiPath}`,
              robots: data.robots || robots || 'index, follow',
              enableDynamicFill: data.enableDynamicFill
            };
            
            // console.log('[SEO] 合併後的數據:', mergedData);
            
            setSeoData(mergedData);
          } catch (error) {
            console.error('[SEO] API 請求錯誤:', {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
              url: apiUrl
            });
            
            // 使用傳入的 props 作為後備
            setSeoData({
              pagePath: apiPath,
              title: title || siteName,
              description: description || '',
              keywords: keywords || '',
              ogTitle: ogTitle || title || siteName,
              ogDescription: ogDescription || description || '',
              ogImage: ogImage || '',
              twitterTitle: twitterTitle || ogTitle || title || siteName,
              twitterDescription: twitterDescription || ogDescription || description || '',
              twitterImage: twitterImage || ogImage || '',
              twitterCard: twitterCard || 'summary_large_image',
              canonicalUrl: canonicalUrl || `${window.location.origin}${apiPath}`,
              robots: robots || 'index, follow',
              enableDynamicFill: enableDynamicFill
            });
          }
        } else {
          console.log('[SEO] 未提供路徑，使用動態值和預設值');
          setSeoData({
            pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
            title: title || siteName,
            description: description || '',
            keywords: keywords || '',
            ogTitle: ogTitle || title || siteName,
            ogDescription: ogDescription || description || '',
            ogImage: ogImage || '',
            canonicalUrl: canonicalUrl || (typeof window !== 'undefined' ? window.location.href : ''),
            robots: 'index, follow'
          });
        }
      } catch (error) {
        console.error('[SEO] 獲取 SEO 數據時發生錯誤:', error);
        // 發生錯誤時使用預設值
        setSeoData({
          pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
          title: title || siteName,
          description: description || '',
          keywords: keywords || '',
          ogTitle: ogTitle || title || siteName,
          ogDescription: ogDescription || description || '',
          ogImage: ogImage || '',
          canonicalUrl: canonicalUrl || (typeof window !== 'undefined' ? window.location.href : ''),
          robots: 'index, follow'
        });
      }
    };

    fetchSEOData();
  }, [path, title, description, keywords, ogTitle, ogDescription, ogImage, canonicalUrl, siteName, enableDynamicFill]);

  // 添加快取清理機制
  useEffect(() => {
    const cleanupCache = () => {
      const now = Date.now();
      Object.keys(seoDataCache).forEach(key => {
        if (now - seoDataCache[key].timestamp > 5 * 60 * 1000) {
          delete seoDataCache[key];
        }
      });
    };

    // 每分鐘清理一次過期的快取
    const interval = setInterval(cleanupCache, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 使用 API 數據或 props 數據
  const seoProps = {
    title: seoData.title || title || siteName,
    description: seoData.description || description || '',
    keywords: seoData.keywords || keywords || '',
    ogTitle: seoData.ogTitle || ogTitle || seoData.title || title || siteName,
    ogDescription: seoData.ogDescription || ogDescription || seoData.description || description || '',
    ogImage: seoData.ogImage || ogImage || '',
    twitterTitle: seoData.twitterTitle || twitterTitle || seoData.ogTitle || ogTitle || seoData.title || title || siteName,
    twitterDescription: seoData.twitterDescription || twitterDescription || seoData.ogDescription || ogDescription || seoData.description || description || '',
    twitterImage: seoData.twitterImage || twitterImage || seoData.ogImage || ogImage || '',
    canonicalUrl: seoData.canonicalUrl || canonicalUrl || (typeof window !== 'undefined' ? window.location.href : ''),
    noIndex: seoData.robots === 'noindex, nofollow' || noIndex
  };

  // 生成完整的標題
  const fullTitle = `${seoProps.title}${seoProps.title.includes(siteName) ? '' : ` | ${siteName}`}`;
  
  // === URL Processing ===
  const fullUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const canonicalUrlToUse = seoProps.canonicalUrl || canonicalUrl || fullUrl;
  
  // === Open Graph Processing ===
  const ogTitleToUse = seoProps.ogTitle || ogTitle || fullTitle;
  
  // === Description Processing ===
  const descriptionToUse = seoProps.description;
  const ogDescriptionToUse = seoProps.ogDescription || ogDescription || seoProps.description;
  const twitterDescriptionToUse = seoProps.twitterDescription || twitterDescription || seoProps.ogDescription || ogDescription || seoProps.description;
  
  const ogTypeToUse = ogType || type;
  const ogUrlToUse = ogUrl || fullUrl;
  const ogImageToUse = seoProps.ogImage || ogImage || image;

  // === Process Custom Meta ===
  const processedCustomMeta = customMeta.map(processCustomMeta);

  // === Validate Custom Meta ===
  const validCustomMeta = processedCustomMeta.filter(meta => {
    if (!meta.content) {
      console.warn('Custom meta tag missing content:', meta);
      return false;
    }
    if (!meta.name && !meta.property) {
      console.warn('Custom meta tag missing both name and property:', meta);
      return false;
    }
    return true;
  });

  return (
    <Helmet>
      {/* === Basic Meta Tags === */}
      <title>{fullTitle}</title>
      {descriptionToUse && <meta name="description" content={descriptionToUse} key={generateMetaId('basic', 'description')} />}
      {seoProps.keywords && <meta name="keywords" content={seoProps.keywords} key={generateMetaId('basic', 'keywords')} />}
      {author && <meta name="author" content={author} key={generateMetaId('basic', 'author')} />}
      <meta name="locale" content={locale} key={generateMetaId('basic', 'locale')} />
      {seoProps.noIndex && <meta name="robots" content="noindex, nofollow" key={generateMetaId('basic', 'robots')} />}
      
      {/* === Open Graph Meta Tags === */}
      {ogTitleToUse && <meta property="og:title" content={ogTitleToUse} key={generateMetaId('og', 'title')} />}
      {ogDescriptionToUse && <meta property="og:description" content={ogDescriptionToUse} key={generateMetaId('og', 'description')} />}
      {ogImageToUse && <meta property="og:image" content={ogImageToUse} key={generateMetaId('og', 'image')} />}
      <meta property="og:type" content={ogTypeToUse} key={generateMetaId('og', 'type')} />
      <meta property="og:site_name" content={siteName} key={generateMetaId('og', 'site-name')} />
      <meta property="og:locale" content={locale} key={generateMetaId('og', 'locale')} />
      {ogUrlToUse && <meta property="og:url" content={ogUrlToUse} key={generateMetaId('og', 'url')} />}
      
      {/* === Image Meta Tags === */}
      {imageWidth && <meta property="og:image:width" content={imageWidth} key={generateMetaId('og', 'image-width')} />}
      {imageHeight && <meta property="og:image:height" content={imageHeight} key={generateMetaId('og', 'image-height')} />}
      {imageAlt && <meta property="og:image:alt" content={imageAlt} key={generateMetaId('og', 'image-alt')} />}
      {imageSecureUrl && <meta property="og:image:secure_url" content={imageSecureUrl} key={generateMetaId('og', 'image-secure')} />}
      
      {/* === Article Meta Tags === */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} key={generateMetaId('article', 'published-time')} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} key={generateMetaId('article', 'modified-time')} />}
          {section && <meta property="article:section" content={section} key={generateMetaId('article', 'section')} />}
          {tags && tags.map((tag, index) => (
            <meta 
              key={generateMetaId('article', 'tag', index.toString())}
              property="article:tag" 
              content={tag} 
            />
          ))}
          {author && <meta property="article:author" content={author} key={generateMetaId('article', 'author')} />}
        </>
      )}
      
      {/* === Twitter Card Meta Tags === */}
      <meta name="twitter:card" content={twitterCard} key={generateMetaId('twitter', 'card')} />
      {seoProps.twitterTitle && <meta name="twitter:title" content={seoProps.twitterTitle} key={generateMetaId('twitter', 'title')} />}
      {twitterDescriptionToUse && <meta name="twitter:description" content={twitterDescriptionToUse} key={generateMetaId('twitter', 'description')} />}
      {seoProps.twitterImage && <meta name="twitter:image" content={seoProps.twitterImage} key={generateMetaId('twitter', 'image')} />}
      {twitterSite && <meta name="twitter:site" content={twitterSite} key={generateMetaId('twitter', 'site')} />}
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} key={generateMetaId('twitter', 'creator')} />}
      {twitterImageAlt && <meta name="twitter:image:alt" content={twitterImageAlt} key={generateMetaId('twitter', 'image-alt')} />}
      
      {/* === Custom Meta Tags === */}
      {validCustomMeta.map((meta) => (
        <meta 
          key={meta.id}
          {...(meta.name ? { name: meta.name } : { property: meta.property })}
          content={meta.content}
        />
      ))}

      {/* === Required Meta Tags === */}
      <meta name="viewport" content="width=device-width, initial-scale=1" key={generateMetaId('basic', 'viewport')} />
      <meta charSet="utf-8" key={generateMetaId('basic', 'charset')} />

      {/* === Canonical URL === */}
      <link rel="canonical" href={canonicalUrlToUse} key={generateMetaId('basic', 'canonical')} />
    </Helmet>
  );
};

export default SEO; 